import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { VehicleService } from '../vehicleService';
import { UserService } from '../../users/userService';
import { CreateVehicleDto } from '../dto/createVehicleDto';
import { ListVehicleDto } from '../dto/listVehicleDto';
import { ListUserDto } from '../../users/dto/listUserDto';
import { RestResponse } from '../../../shared/RestResponse';

@Component({
  selector: 'app-vehicle-form',
  templateUrl: './vehicle-form.html',
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    CheckboxModule,
    MessageModule,
    ProgressSpinnerModule,
    DividerModule
  ]
})
export class VehicleFormComponent implements OnInit {
  vehicleForm!: FormGroup;
  isLoading: boolean = false;
  errorMessage: string = '';
  isEditMode: boolean = false;
  vehicleId: number | null = null;
  drivers: ListUserDto[] = [];

  constructor(
    private vehicleService: VehicleService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.vehicleId = +params['id'];
        this.isEditMode = true;
        // Carrega os motoristas primeiro, depois o veículo
        this.loadDriversAndVehicle();
      } else {
        this.loadDrivers();
      }
    });
  }

  private initializeForm(): void {
    this.vehicleForm = this.fb.group({
      model: ['', [Validators.required, Validators.minLength(2)]],
      brand: ['', [Validators.required, Validators.minLength(2)]],
      licensePlate: ['', [Validators.required, Validators.pattern(/^[A-Z]{3}-[0-9]{4}$/)]],
      seatCount: [0, [Validators.required, Validators.min(1), Validators.max(50)]],
      active: [true],
      driverId: [null]
    });
  }

  private loadDrivers(): void {
    // Assumindo que roleId 2 é para motoristas (você pode ajustar conforme sua lógica)
    this.userService.list('', 1).subscribe({
      next: (response: RestResponse<ListUserDto[]>) => {
        this.drivers = response.data;
      },
      error: (error) => {
        console.error('Erro ao carregar motoristas:', error);
        this.drivers = [];
      }
    });
  }

  private loadDriversAndVehicle(): void {
    // Carrega os motoristas primeiro
    this.userService.list('', 1).subscribe({
      next: (response: RestResponse<ListUserDto[]>) => {
        this.drivers = response.data;
        // Depois que os motoristas são carregados, carrega o veículo
        this.loadVehicle();
      },
      error: (error) => {
        console.error('Erro ao carregar motoristas:', error);
        this.drivers = [];
        // Mesmo com erro, tenta carregar o veículo
        this.loadVehicle();
      }
    });
  }

  private loadVehicle(): void {
    if (this.vehicleId) {
      this.isLoading = true;
      this.vehicleService.getById(this.vehicleId).subscribe({
        next: (response: RestResponse<ListVehicleDto>) => {
          this.vehicleForm.patchValue({
            model: response.data.model,
            brand: response.data.brand,
            licensePlate: response.data.licensePlate,
            seatCount: response.data.seatCount,
            active: response.data.active,
            driverId: response.data.driverId || null
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao carregar dados do veículo.';
          console.error('Erro ao carregar veículo:', error);
        }
      });
    }
  }

  onSubmit(): void {
    if (this.vehicleForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const vehicleData: CreateVehicleDto = this.vehicleForm.value;

      if (this.isEditMode && this.vehicleId) {
        this.updateVehicle(vehicleData);
      } else {
        this.createVehicle(vehicleData);
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private createVehicle(vehicleData: CreateVehicleDto): void {
    this.vehicleService.create(vehicleData).subscribe({
      next: (response: RestResponse<void>) => {
        this.isLoading = false;
        this.router.navigate(['/vehicles']);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Erro ao cadastrar veículo. Tente novamente.';
        console.error('Erro ao criar veículo:', error);
      }
    });
  }

  private updateVehicle(vehicleData: CreateVehicleDto): void {
    if (this.vehicleId) {
      this.vehicleService.update(this.vehicleId, vehicleData).subscribe({
        next: (response: RestResponse<CreateVehicleDto>) => {
          this.isLoading = false;
          this.router.navigate(['/vehicles']);
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Erro ao atualizar veículo. Tente novamente.';
          console.error('Erro ao atualizar veículo:', error);
        }
      });
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.vehicleForm.controls).forEach(key => {
      const control = this.vehicleForm.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.vehicleForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${this.getFieldLabel(fieldName)} é obrigatório`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldLabel(fieldName)} deve ter pelo menos ${field.errors['minlength'].requiredLength} caracteres`;
      }
      if (field.errors['pattern']) {
        return `${this.getFieldLabel(fieldName)} deve estar no formato ABC-1234`;
      }
      if (field.errors['min']) {
        return `${this.getFieldLabel(fieldName)} deve ser maior que ${field.errors['min'].min}`;
      }
      if (field.errors['max']) {
        return `${this.getFieldLabel(fieldName)} deve ser menor que ${field.errors['max'].max}`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'model': 'Modelo',
      'brand': 'Marca',
      'licensePlate': 'Placa',
      'seatCount': 'Número de assentos',
      'active': 'Status',
      'driverId': 'ID do Motorista'
    };
    return labels[fieldName] || fieldName;
  }

  onCancel(): void {
    this.router.navigate(['/vehicles']);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar Veículo' : 'Cadastrar Veículo';
  }

  get submitButtonText(): string {
    if (this.isLoading) {
      return this.isEditMode ? 'Atualizando...' : 'Cadastrando...';
    }
    return this.isEditMode ? 'Atualizar' : 'Cadastrar';
  }
}
